import { useEffect, useState } from 'react';
import { Dialog, Box, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import {
    getTiposMigrables,
    previsualizarCambioTipo,
    cambiarTipoTramite,
} from '../../services/administracionTramiteService';

// Convierte un trámite de un tipo a otro (ej. un PNC mal inferido que en realidad era una
// Jubilación). El trámite conserva su id y todo lo que cuelga de él; lo único que se descarta son
// los campos propios del tipo viejo y su checklist. Nada se manda hasta que el operador ve el
// preview que devuelve el backend y escribe un motivo.

const ETIQUETA_TIPO = {
    JUBILACION: 'Jubilación',
    PUAM: 'PUAM',
    PLAN_PAGO: 'Plan de Pago',
    REAJUSTE: 'Reajuste',
    RECONOCIMIENTO: 'Reconocimiento',
    PNC: 'PNC',
    AMPARO_PNC: 'Amparo PNC',
    AMPARO_LEY_16986: 'Amparo Ley 16.986',
    GENERICO: 'Otro trámite',
    PENSION: 'Pensión',
    GANANCIAS: 'Impuesto a las Ganancias',
    MINUSVALIA: 'Minusvalía',
    DECLARATORIA_HEREDEROS: 'Declaratoria de Herederos',
    // Amparo por mora ante ANSES (V239). Es el destino típico de un Amparo PNC cargado con
    // organismo = ANSES, que en realidad es de este tipo.
    AMPARO: 'Amparo por Mora (ANSES)',
};

const etiqueta = (tipo) => ETIQUETA_TIPO[tipo] || tipo;

function ModalCambioTipoTramite({ open, onClose, idTramite, tipoActual, onCambiado }) {
    const [tipos, setTipos] = useState([]);
    const [tipoDestino, setTipoDestino] = useState('');
    const [preview, setPreview] = useState(null);
    const [cargandoPreview, setCargandoPreview] = useState(false);
    const [camposDestino, setCamposDestino] = useState({});
    const [motivo, setMotivo] = useState('');
    const [guardando, setGuardando] = useState(false);

    // Depende de `open` y no del trámite: así el modal se resetea al abrirse y no se recarga
    // solo en cada render del padre.
    useEffect(() => {
        if (!open) return;
        setTipoDestino('');
        setPreview(null);
        setCamposDestino({});
        setMotivo('');
        getTiposMigrables()
            .then((lista) => setTipos(lista.filter((t) => t !== tipoActual)))
            .catch(() => setTipos([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const elegirDestino = async (nuevoTipo) => {
        setTipoDestino(nuevoTipo);
        setPreview(null);
        setCamposDestino({});
        if (!nuevoTipo) return;
        setCargandoPreview(true);
        try {
            const datos = await previsualizarCambioTipo(idTramite, nuevoTipo);
            setPreview(datos);
        } catch (e) {
            toast.error(e?.response?.data || 'No se pudo calcular qué cambia.');
        } finally {
            setCargandoPreview(false);
        }
    };

    const faltanCampos = (preview?.camposRequeridos || [])
        .some((c) => !(camposDestino[c.campo] || '').trim());
    const puedeConfirmar = !!preview && !!motivo.trim() && !faltanCampos && !guardando;

    const confirmar = async () => {
        if (!puedeConfirmar) return;
        setGuardando(true);
        try {
            await cambiarTipoTramite(idTramite, { tipoDestino, motivo: motivo.trim(), camposDestino });
            toast.success(`El trámite ahora es de tipo ${etiqueta(tipoDestino)}.`);
            onClose();
            if (onCambiado) onCambiado(tipoDestino);
        } catch (e) {
            toast.error(e?.response?.data || 'No se pudo cambiar el tipo de trámite.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <Box sx={{ p: 3 }}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                        <h5 className="fw-bold mb-1">Cambiar el tipo del trámite</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.88rem' }}>
                            Trámite #{idTramite} — hoy es <strong>{etiqueta(tipoActual)}</strong>
                        </p>
                    </div>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </div>

                <div className="mb-3">
                    <label className="form-label fw-semibold" htmlFor="tipoDestino">Convertir a</label>
                    <select
                        id="tipoDestino"
                        className="form-select"
                        value={tipoDestino}
                        onChange={(e) => elegirDestino(e.target.value)}
                        disabled={guardando}
                    >
                        <option value="">Elegí el tipo nuevo…</option>
                        {tipos.map((t) => <option key={t} value={t}>{etiqueta(t)}</option>)}
                    </select>
                    <small className="text-muted">
                        RTI y ART no aparecen: tienen estructura propia y no se pueden convertir.
                    </small>
                </div>

                {cargandoPreview && (
                    <div className="d-flex align-items-center gap-2 text-muted mb-3">
                        <CircularProgress size={18} /> Calculando qué cambia…
                    </div>
                )}

                {preview && (
                    <>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="border rounded-3 p-3 h-100" style={{ background: '#f0fdf4' }}>
                                    <h6 className="fw-bold text-success mb-2">
                                        <i className="bi bi-check-circle-fill me-2" />Se conserva
                                    </h6>
                                    <ul className="mb-0 ps-3" style={{ fontSize: '0.87rem' }}>
                                        {(preview.seConserva || []).map((linea, i) => <li key={i}>{linea}</li>)}
                                    </ul>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="border rounded-3 p-3 h-100" style={{ background: '#fef2f2' }}>
                                    <h6 className="fw-bold text-danger mb-2">
                                        <i className="bi bi-exclamation-triangle-fill me-2" />Se descarta
                                    </h6>
                                    {(preview.sePierde || []).length === 0 && preview.itemsChecklistADescartar === 0 && (
                                        <p className="mb-0 text-muted" style={{ fontSize: '0.87rem' }}>
                                            Nada: el trámite no tiene datos propios de su tipo actual.
                                        </p>
                                    )}
                                    <ul className="mb-0 ps-3" style={{ fontSize: '0.87rem' }}>
                                        {(preview.sePierde || []).map((d, i) => (
                                            <li key={i}><strong>{d.campo}:</strong> {d.valor}</li>
                                        ))}
                                        {preview.itemsChecklistADescartar > 0 && (
                                            <li>{preview.itemsChecklistADescartar} ítem(s) del checklist</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="alert alert-light border mb-3" style={{ fontSize: '0.87rem' }}>
                            <div className="mb-2">
                                <strong>Estado:</strong> {preview.estadoActual} → {preview.estadoDestino}
                            </div>
                            {(preview.advertencias || []).map((a, i) => (
                                <div key={i} className="text-muted">• {a}</div>
                            ))}
                        </div>

                        {(preview.camposRequeridos || []).length > 0 && (
                            <div className="mb-3">
                                <h6 className="fw-bold mb-2">
                                    Datos que pide {etiqueta(tipoDestino)}
                                </h6>
                                <div className="row g-2">
                                    {preview.camposRequeridos.map((campo) => (
                                        <div className="col-md-6" key={campo.campo}>
                                            <label className="form-label" >
                                                {campo.etiqueta} <span className="text-danger">*</span>
                                            </label>
                                            {campo.opciones ? (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={camposDestino[campo.campo] || ''}
                                                    onChange={(e) => setCamposDestino((prev) => ({
                                                        ...prev, [campo.campo]: e.target.value,
                                                    }))}
                                                    disabled={guardando}
                                                >
                                                    <option value="">Elegir…</option>
                                                    {campo.opciones.map((o) => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={camposDestino[campo.campo] || ''}
                                                    onChange={(e) => setCamposDestino((prev) => ({
                                                        ...prev, [campo.campo]: e.target.value,
                                                    }))}
                                                    disabled={guardando}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label fw-semibold" htmlFor="motivoCambioTipo">
                                Motivo del cambio <span className="text-danger">*</span>
                            </label>
                            <textarea
                                id="motivoCambioTipo"
                                className="form-control"
                                rows="2"
                                maxLength={500}
                                value={motivo}
                                onChange={(e) => setMotivo(e.target.value)}
                                placeholder="Ej: mal inferido en la migración de turnos ANSES; era una jubilación."
                                disabled={guardando}
                            />
                            <small className="text-muted">
                                Queda en el historial del trámite y en el registro de auditoría.
                            </small>
                        </div>
                    </>
                )}

                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-outline-secondary" onClick={onClose} disabled={guardando}>
                        Cancelar
                    </button>
                    <button className="btn btn-primary" onClick={confirmar} disabled={!puedeConfirmar}>
                        {guardando ? 'Convirtiendo…' : 'Convertir trámite'}
                    </button>
                </div>
            </Box>
        </Dialog>
    );
}

export default ModalCambioTipoTramite;
